/**
 * `docs/KNOWN-BUGS.md` item 5: "Rewriting HUD text and status chips every
 * frame next to a canvas costs paint for nothing. Throttle to roughly 10Hz."
 *
 * `ThrottledWriter` takes a `nowMs` on every `update` call rather than
 * reading a clock itself, so it stays as pure and testable as the input
 * modules — a test drives it with a simulated clock and counts how many
 * times `write` actually ran. The game loop calls `update` on every frame
 * with the same accumulated time it already tracks for the fixed timestep;
 * this never reaches for `Date.now()` or `performance.now()` on its own.
 */
export class ThrottledWriter<T> {
  private lastWriteMs = -Infinity;
  private hasPending = false;
  private pending: T | undefined;

  constructor(
    private readonly intervalMs: number,
    private readonly write: (value: T) => void,
  ) {}

  /**
   * Record the latest value. Writes immediately the first time, or once
   * `intervalMs` has passed since the last write; otherwise the value is
   * held until the next call that crosses the interval, or `flush`.
   */
  update(value: T, nowMs: number): void {
    this.pending = value;
    this.hasPending = true;
    if (nowMs - this.lastWriteMs >= this.intervalMs) this.flush(nowMs);
  }

  /** Force whatever is pending out immediately — for a screen transition or a final HUD state. */
  flush(nowMs: number): void {
    if (!this.hasPending) return;
    this.write(this.pending as T);
    this.hasPending = false;
    this.pending = undefined;
    this.lastWriteMs = nowMs;
  }
}
